import { useState, type FormEvent } from 'react';
import { Markdown } from '../../components/Markdown';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import { ag5Dict } from './i18n';
import type { ScriptDefinition } from './scripts';

/**
 * Shared shell for every Smalltalk generator page. The generators are pure
 * string builders with no database access, so they run in the browser and the
 * output appears on submit, with no request involved.
 */
export function ScriptPage({ script }: { script: ScriptDefinition }) {
  const t = useDict(ag5Dict);
  const { locale } = useLocale();
  const { fields, generate } = script;
  const text = script.text[locale];
  usePageTitle(t.scriptTitle(text.title));
  const [values, setValues] = useState<Record<string, string>>({});
  const [code, setCode] = useState<string | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCode(generate(values));
  }

  return (
    <>
      <PageHeader title={text.title} />

      <Panel>
        <div className="form-hint">
          <Markdown>{text.description}</Markdown>
        </div>
        <form onSubmit={onSubmit} className="form-stack">
          {fields.map((field) => (
            <div key={field.name}>
              <label htmlFor={field.id} className="field-label">
                {text.fields[field.name].label}
              </label>
              <input
                id={field.id}
                name={field.name}
                type="text"
                autoComplete="off"
                placeholder={text.fields[field.name].placeholder}
                className="field-input"
                value={values[field.name] ?? ''}
                onChange={(e) =>
                  setValues((current) => ({
                    ...current,
                    [field.name]: e.target.value,
                  }))
                }
              />
            </div>
          ))}
          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {t.generate}
            </button>
          </div>
        </form>
      </Panel>

      <div id="script-output">
        {code !== null && <ScriptOutput code={code} />}
      </div>
    </>
  );
}

function ScriptOutput({ code }: { code: string }) {
  const t = useDict(ag5Dict);
  const [copied, setCopied] = useState(false);

  return (
    <Panel>
      <div className="script-output-toolbar">
        <h2 className="heading-section">{t.generatedScript}</h2>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            void navigator.clipboard.writeText(code).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? t.copied : t.copy}
        </button>
      </div>
      <pre className="script-output-pre">
        <code>{code}</code>
      </pre>
    </Panel>
  );
}
